import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Debug logging for environment variable
console.log('🔍 Backend URL Configuration:', {
  REACT_APP_BACKEND_URL: BACKEND_URL,
  timestamp: new Date().toISOString()
});

if (!BACKEND_URL || BACKEND_URL === 'undefined') {
  console.error('❌ CRITICAL: REACT_APP_BACKEND_URL is not defined!');
  console.error('Current value:', BACKEND_URL);
  console.error('All env vars:', process.env);
}

export const API_URL = `${BACKEND_URL}/api`;
console.log('🔍 Full API URL:', API_URL);

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: false, // Changed to false to avoid CORS issues with wildcard origins
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to include session token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // Log the full request URL for debugging
  console.log('🔵 API Request:', config.method?.toUpperCase(), config.baseURL + config.url);
  return config;
}, (error) => {
  console.error('❌ API Request Error:', error);
  return Promise.reject(error);
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.url, response.status);
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('❌ API Error Response:', {
        url: error.config?.url,
        status: error.response.status,
        data: error.response.data,
      });
    } else if (error.request) {
      // Request made but no response
      console.error('❌ API No Response:', {
        url: error.config?.url,
        message: 'Server did not respond. Check if backend URL is correct.',
        backendUrl: BACKEND_URL,
      });
    } else {
      // Error setting up request
      console.error('❌ API Setup Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Auth
export const signup = (data) => api.post('/auth/signup', data);
export const login = (data) => api.post('/auth/login', data);
export const exchangeSession = (sessionId) => api.post('/auth/session', { session_id: sessionId });
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');

// Questions
export const getQuestionsForLevel = (level) => api.get(`/questions/level/${level}`);
export const generateQuestions = () => api.post('/questions/generate');

// Game
export const getGameProgress = () => api.get('/game/progress');
export const completeLevel = (data) => api.post('/game/complete-level', data);
export const getLeaderboard = () => api.get('/game/leaderboard');
export const checkLossCondition = () => api.get('/game/check-loss');
export const getRoundStatus = () => api.get('/game/round-status');
export const startRound2 = () => api.post('/game/start-round-2');
export const resetGameNew = () => api.post('/game/reset-game');
export const addBonusMembers = (bonusAmount = 200) => api.post('/game/add-bonus-members', { bonus_amount: bonusAmount });

// Payment
export const createCheckout = (originUrl) => api.post('/payment/checkout', { origin_url: originUrl });
export const getPaymentStatus = (sessionId) => api.get(`/payment/status/${sessionId}`);

// Settings
export const submitSuggestion = (text) => api.post('/settings/suggestion', { suggestion_text: text });
export const resetGame = (keepHighScore = false) => api.post('/settings/reset', null, { params: { keep_high_score: keepHighScore } });

// Guest
export const createGuest = () => api.post('/guest/create');

// Admin
export const getAllUsers = () => api.get('/admin/users');
export const getAdminStats = () => api.get('/admin/stats');

// Christmas Tournament
export const getTournamentInfo = () => api.get('/tournament/info');
export const getTournamentStatus = () => api.get('/tournament/status');
export const enterTournament = () => api.post('/tournament/enter');
export const confirmTournamentPayment = (sessionId) => api.get('/tournament/payment-success', { params: { session_id: sessionId } });
export const getTournamentLeaderboard = () => api.get('/tournament/leaderboard');
export const getTournamentWinners = () => api.get('/tournament/winners');
export const getTournamentQuestions = (level) => api.get(`/tournament/questions/${level}`);
export const submitTournamentAnswers = (level, answers) => api.post('/tournament/submit', { level, answers });
export const deleteUser = (userEmail) => api.delete(`/admin/users/${userEmail}`);